import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import { SEX_LIST } from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppEstimatedAgeTextField from '@/common/components/form-inputs/AppEstimatedAgeTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useGetStaffAllListQuery } from '@/api/common';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditReferralForICCMWForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const userInfo = useSelector(selectMyInfo);

		const defaultValues: DefaultValues<any> = {
			sex: null,
		};

		const { data: staffList }: any = useGetStaffAllListQuery(
			userInfo?.currentBranch?.id || '',
			{
				refetchOnMountOrArgChange: true,
				skip: !userInfo?.currentBranch,
			}
		);

		useEffect(() => {
			if (details) {
				methods.reset(details);
				if (!details.lookIntoCase) methods.setValue('lookIntoCaseDate', '');
				if (!details.caseHasBeenTakenUp) {
					methods.setValue('nameOfSocialWorker', '');
					methods.setValue('phoneOfSocialWorker', '');
				}
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			referralDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			nameOfSocialWorker: Yup.string().when('caseHasBeenTakenUp', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			phoneOfSocialWorker: Yup.string()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits')))
				.when('caseHasBeenTakenUp', {
					is: (is: boolean) => is,
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}),
			contactTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			lookIntoCaseDate: Yup.string().when('lookIntoCase', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			notBeFollowedUpBecause: Yup.string().when('notBeFollowedUp', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			othersSpecify: Yup.string().when('others', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchCaseHasBeenTakenUp = methods.watch('caseHasBeenTakenUp');
		const watchLookIntoCase = methods.watch('lookIntoCase');
		const watchNotBeFollowedUpBecause = methods.watch('notBeFollowedUp');
		const watchOthersSpecify = methods.watch('others');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.receiptOfReferralForICCMW')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameEn'
									label={t('preAdmission.name')}
								/>
							</Grid>
							<Grid size={6}></Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='hkicNo'
									label={t('preAdmission.hkicNo')}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.sex')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='sex'
									row
									options={SEX_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppEstimatedAgeTextField
									control={methods.control}
									label={t('preAdmission.age')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='referralDate'
									label={t('preAdmission.inRespectOfAboveReferralOn')}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.pleaseBeInformedThat')}
								</Typography>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='caseHasBeenTakenUp'
										label={t('preAdmission.caseHasBeenTakenUpBy')}
										handleChange={(e: any) => {
											if (e) {
												methods.setValue(
													'nameOfSocialWorker',
													details.nameOfSocialWorker
												);
												methods.setValue(
													'phoneOfSocialWorker',
													details.phoneOfSocialWorker
												);
											} else {
												methods.setValue('nameOfSocialWorker', '');
												methods.setValue('phoneOfSocialWorker', '');
												methods.clearErrors('nameOfSocialWorker');
												methods.clearErrors('phoneOfSocialWorker');
											}
										}}
									/>
									<AppTextField
										disabled={!watchCaseHasBeenTakenUp}
										variantType={'standard'}
										control={methods.control}
										name='nameOfSocialWorker'
										sx={{
											position: 'relative',
											top: '-10px',
											marginRight: '10px',
										}}
										label={`${t('preAdmission.nameSocialWorker')}${
											watchCaseHasBeenTakenUp ? '*' : ''
										}`}
									/>
									<AppTextField
										disabled={!watchCaseHasBeenTakenUp}
										sx={{ position: 'relative', top: '-10px' }}
										variantType={'standard'}
										control={methods.control}
										name='phoneOfSocialWorker'
										label={`${t('preAdmission.phoneNo')}${
											watchCaseHasBeenTakenUp ? '*' : ''
										}`}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='lookIntoCase'
										label={t('preAdmission.ourOfficeWillLookIntoCase')}
										handleChange={(e: any) => {
											if (e) {
												methods.setValue(
													'lookIntoCaseDate',
													details.lookIntoCaseDate
												);
											} else {
												methods.setValue('lookIntoCaseDate', '');
												methods.clearErrors('lookIntoCaseDate');
											}
										}}
									/>
									<AppDatePicker
										disabled={!watchLookIntoCase}
										control={methods.control}
										name='lookIntoCaseDate'
										variantType={'standard'}
										sx={{ position: 'relative', top: '-10px' }}
										label={`${t('preAdmission.date_')}${
											watchLookIntoCase ? '*' : ''
										}`}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='notBeFollowedUp'
										label={t('preAdmission.caseWillNotFollowedByICCMWBecause')}
										handleChange={() => {
											methods.setValue('notBeFollowedUpBecause', '');
											methods.clearErrors('notBeFollowedUpBecause');
										}}
									/>
									<AppTextField
										disabled={!watchNotBeFollowedUpBecause}
										variantType={'standard'}
										control={methods.control}
										name='notBeFollowedUpBecause'
										sx={{ position: 'relative', top: '-10px' }}
										label={`${t('preAdmission.because')}${
											watchNotBeFollowedUpBecause ? '*' : ''
										}`}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='others'
										label={t('preAdmission.othersSpecify')}
										handleChange={() => {
											methods.setValue('othersSpecify', '');
											methods.clearErrors('othersSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchOthersSpecify}
										variantType={'standard'}
										control={methods.control}
										name='othersSpecify'
										sx={{ position: 'relative', top: '-10px' }}
										label={`${t('preAdmission.specify')}${
											watchOthersSpecify ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
						</Grid>
						<Divider sx={{ margin: '0 0 24px' }} />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.shouldEnquiries')}
								</Typography>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='contactWorker'
									label={t('preAdmission.name')}
									options={staffList?.data || []}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='contactTel'
									label={t('preAdmission.telNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='contactOfficerInCharge'
									label={t('preAdmission.officerInCharge')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditReferralForICCMWForm.displayName = 'EditReferralForICCMWForm';

export default EditReferralForICCMWForm;
